import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { VerjaError } from '@/models/ErrorResponse'

export interface UseFormErrorsOptions {
    messages?: Record<string, string>
}

export function useFormErrors(options: UseFormErrorsOptions = {}) {
    const { t } = useI18n()
    const messages = options.messages ?? {}

    const fieldErrors = ref<Record<string, VerjaError[]>>({})

    function translateFieldError(name: string, error: VerjaError): string {
        const msgKey = messages[`${name}.${error.key}`]
        if (msgKey) {
            return msgKey
        }

        const verjaKey = `verja.${error.key}`
        const translated = t(verjaKey, error.parameters ?? {})
        if (translated !== verjaKey) {
            return translated
        }

        return error.message ?? ''
    }

    function getFieldMessages(name: string): string[] {
        const errors = fieldErrors.value[name]
        if (!errors || errors.length === 0) {
            return []
        }
        return errors.map(error => translateFieldError(name, error))
    }

    function hasFieldError(name: string): boolean {
        return (fieldErrors.value[name]?.length ?? 0) > 0
    }

    function setErrors(errors: Record<string, VerjaError[]>): void {
        clearErrors()
        fieldErrors.value = errors
    }

    function addFieldErrors(name: string, errors: VerjaError[]): void {
        fieldErrors.value[name] = errors
    }

    function clearErrors(): void {
        fieldErrors.value = {}
    }

    function clearField(name: string): void {
        if (name in fieldErrors.value) {
            delete fieldErrors.value[name]
        }
    }

    return {
        getFieldMessages,
        hasFieldError,
        setErrors,
        addFieldErrors,
        clearErrors,
        clearField,
    }
}
